/**
 * The authority report of one stage of a vulnerability case (DEV-60).
 *
 * Reports are entered by hand in the web form of the ENISA Single Reporting
 * Platform; this panel shows every field in the order of the form, plus
 * „Alles kopieren“ and a text download. The values come from the
 * case — core facts, authority fields, assessments, PSIRT team — and are
 * corrected there. Once the report is recorded, the panel shows what was
 * reported. A missing value is a hint, never a lock.
 */
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { useEffect, useState, type ReactNode } from 'react';

import Banner from '../../components/Banner';
import Chip from '../../components/Chip';
import Spinner from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { errorMessage, fetchCaseReport } from '../../lib/api';
import { fmtDate } from '../../lib/format';
import type { AuthorityReport, ReportFieldSource, ReportStage } from '../../lib/types';
import { backendLabel } from '../../lib/backendLabels';

const SOURCE_HINTS: Readonly<Record<ReportFieldSource, MessageDescriptor>> = {
  case: msg`In den Kernangaben ergänzen`,
  psirt: msg`Beim PSIRT-Team des Produkts ergänzen`,
  cmdb: msg`In den Stammdaten ergänzen`,
  assessment: msg`Ergibt sich aus Bewertungen und Kundenländern`,
};
const SOURCE_ORDER: readonly ReportFieldSource[] = ['case', 'psirt', 'cmdb', 'assessment'];

/** What is missing, one line per place where it is entered (DEV-103). */
function MissingBySource({ fields, onEditFacts }: Readonly<{
  fields: AuthorityReport['fields'];
  onEditFacts?: () => void;
}>): ReactNode {
  const { t, i18n } = useLingui();
  const groups = SOURCE_ORDER
    .map((source) => ({ source, labels: fields.filter((f) => f.missing && f.source === source).map((f) => f.label) }))
    .filter((group) => group.labels.length > 0);
  if (groups.length === 0) return null;
  return (
    <ul className="case-report-missing" aria-label={t`Fehlende Angaben`}>
      {groups.map((group) => (
        <li key={group.source}>
          <span>{i18n._(SOURCE_HINTS[group.source])}: {group.labels.join(', ')}</span>
          {group.source === 'case' && onEditFacts ? (
            <button type="button" className="link" onClick={onEditFacts}><Trans>Zu den Kernangaben</Trans></button>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

function download(text: string, filename: string): void {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function AuthorityReportPanel({ caseId, stage, id, actions = null, onEditFacts }: Readonly<{
  caseId: number;
  stage: ReportStage;
  id: string;
  /** The case's action for this stage, at the end of the action bar (DEV-103). */
  actions?: ReactNode;
  /** Opens the core facts of the case, offered where a missing value is entered. */
  onEditFacts?: () => void;
}>): ReactNode {
  const { t, i18n } = useLingui();
  const toast = useToast();
  // DEV-103: the previous stage stays on screen until the next one is loaded,
  // so switching stages does not collapse the dialog for a moment.
  const [loaded, setLoaded] = useState<{ stage: ReportStage; report: AuthorityReport } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const report = loaded?.report ?? null;
  const stale = loaded !== null && loaded.stage !== stage;

  useEffect(() => {
    let active = true;
    setError(null);
    fetchCaseReport(caseId, stage)
      .then((result) => { if (active) setLoaded({ stage, report: result }); })
      .catch((err: unknown) => { if (active) setError(errorMessage(err)); });
    return () => { active = false; };
  }, [caseId, stage]);

  function copy(text: string): void {
    navigator.clipboard.writeText(text).then(
      () => toast.success(t`Meldung kopiert.`),
      () => toast.error(t`Kopieren nicht möglich — bitte von Hand markieren.`),
    );
  }

  if (error) return <Banner kind="error"><Trans>Meldung konnte nicht geladen werden: {error}</Trans></Banner>;
  if (report === null) return <p><output><Spinner /> <Trans>Meldung wird vorbereitet…</Trans></output></p>;
  const label = backendLabel(report.label);
  const submittedAt = fmtDate(report.submitted_at);
  const submittedBy = report.submitted_by ?? t`unbekannt`;
  return (
    <section id={id} className={`case-report${stale ? ' is-loading' : ''}`} aria-busy={stale}
      aria-label={t`${label} für die SRP`}>
      {report.saved ? (
        <Banner kind="info" sub={report.changed_since ? t`Die Angaben im Fall haben sich seither geändert.` : undefined}>
          <Trans>Gemeldet {submittedAt} · {submittedBy}. Angezeigt wird der gemeldete Inhalt.</Trans>
        </Banner>
      ) : null}
      {!report.saved && report.submitted_at ? (
        <Banner kind="info">
          <Trans>
            Gemeldet {submittedAt}; der Inhalt wurde damals nicht gespeichert. Angezeigt werden die aktuellen Angaben
            des Falls.
          </Trans>
        </Banner>
      ) : null}
      {report.saved ? null : <MissingBySource fields={report.fields} onEditFacts={onEditFacts} />}
      <dl className="case-report-fields">
        {report.fields.map((field) => (
          <div key={field.key} className="case-report-field">
            <dt>
              {field.label}
              {field.required ? null : <span className="muted"> <Trans>(soweit bekannt)</Trans></span>}
            </dt>
            <dd>
              {field.value ? <span className="case-report-value">{field.value}</span> : null}
              {!field.value && field.missing ? <Chip tone="warn" title={i18n._(SOURCE_HINTS[field.source])}><Trans>fehlt</Trans></Chip> : null}
              {!field.value && !field.missing ? <span className="muted">—</span> : null}
            </dd>
          </div>
        ))}
      </dl>
      <div className="assessment-form-actions">
        <button type="button" className="ghost" onClick={() => copy(report.text)}><Trans>Alles kopieren</Trans></button>
        <button type="button" className="ghost"
          onClick={() => download(report.text, `${stage}-fall-${caseId}.txt`)}>
          <Trans>Als Text herunterladen</Trans>
        </button>
        <div className="spacer" />
        {actions}
      </div>
    </section>
  );
}
